// SPDX-License-Identifier: AGPL-3.0-or-later

import {getReactionKey, type ReactionEmoji} from '@app/features/messaging/utils/ReactionUtils';

const CHANNEL_MESSAGES_SCROLLER = '[data-flx="channel.messages.scroller-inner"]';
const MESSAGE_FALLBACK_X_FROM_LEFT = 48;
const MESSAGE_FALLBACK_Y_FROM_BOTTOM = 12;
const CHIP_RETRY_DELAY_MS = 50;

function cssEscape(value: string): string {
	if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
		return CSS.escape(value);
	}
	return value.replace(/["\\]/g, '\\$&');
}

function centerOf(element: Element): {x: number; y: number} {
	const rect = element.getBoundingClientRect();
	return {x: rect.left + rect.width / 2, y: rect.top + rect.height / 2};
}

function queryPreferred(selector: string): Element | null {
	const scroller = document.querySelector(CHANNEL_MESSAGES_SCROLLER);
	return scroller?.querySelector(selector) ?? document.querySelector(selector);
}

function findMessageElement(channelId: string, messageId: string): Element | null {
	const messageSelector = `[data-message-id="${cssEscape(messageId)}"]`;
	const channelMessageSelector = `[data-channel-id="${cssEscape(channelId)}"]${messageSelector}`;
	return queryPreferred(channelMessageSelector) ?? queryPreferred(messageSelector);
}

export function findReactionChipOrigin(
	channelId: string,
	messageId: string,
	emoji: ReactionEmoji,
): {x: number; y: number} | null {
	const key = getReactionKey(messageId, emoji);
	const chipSelector = `[data-reaction-key="${cssEscape(key)}"]`;
	const message = findMessageElement(channelId, messageId);
	const chip = message?.querySelector(chipSelector) ?? queryPreferred(chipSelector);
	if (!chip) return null;
	const emojiImage = chip.querySelector('img');
	return centerOf(emojiImage ?? chip);
}

export function findReactionMessageFallbackOrigin(channelId: string, messageId: string): {x: number; y: number} | null {
	const message = findMessageElement(channelId, messageId);
	if (!message) return null;
	const rect = message.getBoundingClientRect();
	return {x: rect.left + MESSAGE_FALLBACK_X_FROM_LEFT, y: rect.bottom - MESSAGE_FALLBACK_Y_FROM_BOTTOM};
}

/**
 * Reaction chips often land one paint after the add event. Wait two frames, then retry once,
 * then fall back to the message box so a heart ghost / bat rise still has an origin.
 */
export function withReactionChipOrigin(
	channelId: string,
	messageId: string,
	emoji: ReactionEmoji,
	onOrigin: (origin: {x: number; y: number}) => void,
): void {
	requestAnimationFrame(() => {
		requestAnimationFrame(() => {
			const chipOrigin = findReactionChipOrigin(channelId, messageId, emoji);
			if (chipOrigin) {
				onOrigin(chipOrigin);
				return;
			}
			window.setTimeout(() => {
				const retriedChipOrigin = findReactionChipOrigin(channelId, messageId, emoji);
				if (retriedChipOrigin) {
					onOrigin(retriedChipOrigin);
					return;
				}
				const fallbackOrigin = findReactionMessageFallbackOrigin(channelId, messageId);
				if (fallbackOrigin) onOrigin(fallbackOrigin);
			}, CHIP_RETRY_DELAY_MS);
		});
	});
}

const OVERLAY_SCOPE_SELECTOR = '[data-flx="app.app.app-wrapper.overlay-scope"]';
const HOLIDAY_FLYBY_RISE_SELECTOR = '[data-flx="holiday.holiday-layer.flybys-rise"]';

export function toOverlayScopeOrigin(origin: {x: number; y: number}): {x: number; y: number} {
	const layer = document.querySelector(HOLIDAY_FLYBY_RISE_SELECTOR) ?? document.querySelector(OVERLAY_SCOPE_SELECTOR);
	if (!(layer instanceof HTMLElement)) return origin;
	const rect = layer.getBoundingClientRect();
	return {x: origin.x - rect.left, y: origin.y - rect.top};
}
